/* ============================================================================
 * sp-funnel-clarity.js  —  Stienhardt ring-builder funnel instrumentation
 * ----------------------------------------------------------------------------
 * Fires Microsoft Clarity custom EVENTS + TAGS at every stage and leak point of
 * the 3-step ring builder, so the funnel can be reconstructed in the Clarity
 * dashboard (Settings -> Funnels) and leak sessions watched in Recordings.
 *
 * DESIGN PRINCIPLE: this script is a pure OUTSIDE OBSERVER. It never edits or
 * depends on the internals of finalproduct.js / diamonddetail.js. It detects the
 * real conversion (POST /cart/add.js) by wrapping fetch, and detects silent
 * failures (the commented-out alert in finalproduct.js line 375) via a watchdog
 * + global error capture. It can NOT break the cart flow — every hook is in a
 * try/catch and always passes the original call through.
 *
 * Server context is injected by snippet sp-funnel-clarity.liquid as
 * window.SP_FUNNEL_CTX (template/handle/type) — Liquid is the source of truth for
 * page classification; the DOM .process-steps active step is the tie-breaker.
 * ========================================================================== */
(function () {
  'use strict';
  if (window.__spFunnelInit) return;          // guard against double-load
  window.__spFunnelInit = true;

  var CTX = window.SP_FUNNEL_CTX || {};
  var SS = 'sp_funnel_';                       // sessionStorage namespace

  /* ---- safe Clarity bridge: queue until window.clarity is ready ---------- */
  var q = [];
  function flush() {
    if (typeof window.clarity !== 'function') return;
    while (q.length) { try { window.clarity.apply(window, q.shift()); } catch (e) {} }
  }
  function cl5() { // clarity(...args) but queued + never throws
    try {
      var a = Array.prototype.slice.call(arguments);
      if (typeof window.clarity === 'function') { window.clarity.apply(window, a); }
      else { q.push(a); }
    } catch (e) {}
  }
  // Clarity loads from the app block at content_for_header; poll briefly for it.
  // Once confirmed, also drain the cross-page replay store (see evtDurable) so a
  // terminal event fired right before a navigation isn't lost.
  var tries = 0;
  var poll = setInterval(function () {
    tries++;
    if (typeof window.clarity === 'function') { flush(); replayPending(); clearInterval(poll); }
    else if (tries > 40) { clearInterval(poll); }   // ~20s, then give up quietly
  }, 500);
  if (typeof window.clarity === 'function') { replayPending(); }   // attempt immediately too

  function evt(name)      { cl5('event', name); }
  function tag(k, v)      { if (v != null && v !== '') cl5('set', k, String(v)); }
  function keep(reason)   { cl5('upgrade', reason); }     // force-keep recording
  function fireOnce(key, name) {                          // once per page load
    if (window['__spOnce_' + key]) return; window['__spOnce_' + key] = 1; evt(name);
  }
  function leak(name, which) {
    evt(name);
    tag('builder_leak', which);
    keep('builder_leak');                                 // always keep leak recordings
  }

  /* Durable delivery for the two NAVIGATION-TERMINAL events (add-to-cart success,
     reached checkout): they fire microseconds before a full-page navigation, so if
     the real Clarity bundle hasn't drained its queue yet the event would die with the
     document. evtDurable fires immediately AND persists to sessionStorage; replayPending
     re-fires on the next themed page (always /cart for success) once Clarity is up.
     Clarity funnels dedup per session, so a replayed duplicate is harmless. */
  var PEND = SS + 'pending_evt';
  function evtDurable(name) {
    evt(name);
    try {
      var arr = JSON.parse(sessionStorage.getItem(PEND) || '[]');
      arr.push(name);
      if (arr.length > 10) arr = arr.slice(-10);
      sessionStorage.setItem(PEND, JSON.stringify(arr));
    } catch (e) {}
  }
  function replayPending() {
    try {
      var arr = JSON.parse(sessionStorage.getItem(PEND) || '[]');
      if (!arr.length) return;
      sessionStorage.removeItem(PEND);                    // remove first so re-entry can't loop
      arr.forEach(function (n) { evt(n); });
    } catch (e) {}
  }

  /* ---- query-string helper ---------------------------------------------- */
  function qp(name) {
    try { return new URLSearchParams(window.location.search).get(name) || ''; }
    catch (e) { return ''; }
  }

  /* mobile / tablet / desktop — UA-first (handles iPadOS-reports-as-Mac).
     Buckets deliberately match Shopify's session_device_type values
     (mobile|tablet|desktop) so the Clarity device funnel reconciles 1:1 with
     the ShopifyQL device funnel. */
  function deviceType() {
    try {
      var ua = navigator.userAgent || '';
      if (/iPad/.test(ua) || /Tablet|PlayBook|Silk/i.test(ua) ||
          (/Android/.test(ua) && !/Mobile/.test(ua)) ||
          (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return 'tablet';
      if (/Mobi|iPhone|iPod|Android.*Mobile|Windows Phone|BlackBerry/i.test(ua)) return 'mobile';
      return 'desktop';
    } catch (e) { return 'desktop'; }
  }

  /* ---- enrichment helpers ----------------------------------------------- */
  // price-band thresholds come from a theme setting (no hardcoded magic numbers);
  // default only if the setting is blank.
  var PB = String(CTX.priceBands || '').split(',').map(function (x) { return parseFloat(x); })
             .filter(function (x) { return x > 0; });
  if (!PB.length) PB = [2000, 4000, 8000, 15000];
  function kfmt(n) { return n >= 1000 ? (n / 1000) + 'k' : String(n); }
  function band(v, th) {                 // -> '0-2k' | '2-4k' | ... | '15k+'
    v = parseFloat(v) || 0; var lo = 0;
    for (var i = 0; i < th.length; i++) { if (v < th[i]) return kfmt(lo) + '-' + kfmt(th[i]); lo = th[i]; }
    return kfmt(lo) + '+';
  }
  function parsePrice(t) { return parseFloat(String(t || '').replace(/[^0-9.]/g, '')) || 0; }
  // set a session tag AT MOST ONCE — Clarity appends repeat set() calls into a
  // multi-valued tag, which we don't want for these single-value dimensions.
  function setTagOnce(key, val) {
    if (val == null || val === '') return;
    try { if (sessionStorage.getItem(SS + 't_' + key)) return; sessionStorage.setItem(SS + 't_' + key, '1'); } catch (e) {}
    tag(key, val);
  }

  /* ---- 1) RESOLVE THE BUILDER STAGE ------------------------------------- *
   * Priority: final-ring > diamond PDP > setting PDP(step1) > diamond catalog
   *           > setting catalog > cart. Liquid context + DOM active step.    */
  function activeStepData() {
    try {
      var el = document.querySelector('.process-steps .step.active');
      return el ? (el.getAttribute('data-step') || '') : '';
    } catch (e) { return ''; }
  }
  function resolveStage() {
    var t = CTX.templateSuffix || '';
    var pt = CTX.pageType || '';
    var ptype = CTX.productType || '';
    var phandle = CTX.productHandle || '';
    var chandle = CTX.collectionHandle || '';
    var isFinalRing = /[?&]view=final-ring/.test(window.location.search) || activeStepData() === 'final-ring';
    var isDiamondPDP = phandle === 'diamonds' || ptype === 'Diamond';
    var isProduct = pt === 'product';

    if (isProduct && isFinalRing)                return 'step3_final_ring';
    if (isProduct && isDiamondPDP)               return 'step2_diamond_pdp';
    // Step-1 setting PDP requires POSITIVE builder evidence — the server-rendered
    // ring-setting step OR the #choosethissetting CTA. Do NOT fall back to
    // product.type=='Ring' alone: that misclassifies wedding bands and direct-buy
    // rings (which suppress .process-steps and render #directadtocart instead).
    if (isProduct && (activeStepData() === 'ring-setting' ||
                      document.getElementById('choosethissetting'))) return 'step1_setting_pdp';

    if (pt === 'collection') {
      if (t === 'lab-diamonds') return 'step2_diamond_catalog';
      if (chandle === 'engagement-rings' || t === 'settings' ||
          typeof window.SP_BASE_COLLECTION_URL !== 'undefined') return 'step1_setting_catalog';
    }
    if (pt === 'cart')                           return 'cart';
    return '';   // not a builder page
  }

  var STAGE = resolveStage();

  /* ---- 2) STAGE PAGEVIEW EVENT + SESSION-LEVEL TAGS --------------------- */
  var STAGE_EVENT = {
    step1_setting_catalog: 'bld_step1_setting_catalog',
    step1_setting_pdp:     'bld_step1_setting_pdp',
    step2_diamond_catalog: 'bld_step2_diamond_catalog',
    step2_diamond_pdp:     'bld_step2_diamond_pdp',
    step3_final_ring:      'bld_step3_final_ring',
    cart:                  'bld_cart_reached'
  };

  if (STAGE) {
    keep('ring_builder');                       // keep every builder-session recording
    tag('builder_stage', STAGE);
    tag('builder_device', deviceType());   // phone | tablet | desktop
    evt(STAGE_EVENT[STAGE] || ('bld_' + STAGE));

    // entry point: which step did this session touch FIRST?
    try {
      if (!sessionStorage.getItem(SS + 'entry')) {
        var entry = STAGE.indexOf('step2_diamond') === 0 ? 'diamond_first'
                  : STAGE.indexOf('step1_setting') === 0 ? 'setting_first' : 'other';
        sessionStorage.setItem(SS + 'entry', entry);
      }
      tag('builder_entry', sessionStorage.getItem(SS + 'entry'));
    } catch (e) {}

    // identifiers for cross-referencing recordings
    if (STAGE === 'step2_diamond_pdp') tag('builder_diamond_sku', qp('sku'));
    if (STAGE === 'step3_final_ring')  { tag('builder_diamond_sku', qp('diamond')); tag('builder_setting_handle', (window.location.pathname || '')); }
    if (STAGE === 'step1_setting_pdp') tag('builder_setting_handle', (window.location.pathname || ''));
    // --- WHO: traffic source / paid / new-vs-returning / logged-in (set once) ---
    try {
      if (!sessionStorage.getItem(SS + 'src')) {
        var qs = new URLSearchParams(window.location.search);
        var src = qs.get('utm_source') || '';
        var paid = !!(qs.get('gclid') || qs.get('ttclid') || qs.get('fbclid') || /cpc|paid|ppc/i.test(qs.get('utm_medium') || ''));
        if (!src) {
          if (qs.get('ttclid')) src = 'tiktok-paid';
          else if (qs.get('gclid')) src = 'google-paid';
          else if (qs.get('fbclid')) src = 'meta-paid';
          else { var ref = document.referrer || ''; src = ref ? (ref.split('/')[2] || 'referral') : 'direct'; }
        }
        sessionStorage.setItem(SS + 'src', src);
        sessionStorage.setItem(SS + 'paid', paid ? 'yes' : 'no');
        if (qs.get('utm_campaign')) sessionStorage.setItem(SS + 'camp', qs.get('utm_campaign'));
      }
      setTagOnce('builder_source', sessionStorage.getItem(SS + 'src'));
      setTagOnce('builder_is_paid', sessionStorage.getItem(SS + 'paid'));
      setTagOnce('builder_campaign', sessionStorage.getItem(SS + 'camp'));
    } catch (e) {}
    try {
      var seen = localStorage.getItem('sp_funnel_seen');
      setTagOnce('builder_visitor', seen ? 'returning' : 'new');
      if (!seen) localStorage.setItem('sp_funnel_seen', '1');
    } catch (e) {}
    // logged-in customer id = stable cross-device identity (Shopify's numeric id, not PII)
    if (CTX.customerId) { setTagOnce('builder_customer', String(CTX.customerId)); cl5('identify', 'cust_' + CTX.customerId); }

    // --- WHAT: diamond vendor / shape / price band — captured from the catalog
    //     card on click (stashed in sessionStorage), applied session-wide here. ---
    try {
      var dia = JSON.parse(sessionStorage.getItem(SS + 'dia') || 'null');
      if (dia) {
        setTagOnce('builder_vendor', dia.vendor);
        setTagOnce('builder_shape', dia.shape);
        if (dia.price) setTagOnce('builder_price_band', band(dia.price, PB));
      }
      if (STAGE === 'step3_final_ring') {        // fallback shape from _diamonmd cookie
        var ck = (document.cookie.match(/(?:^|;\s*)_diamonmd=([^;]+)/) || [])[1];
        if (ck) { try { var cd = JSON.parse(decodeURIComponent(ck)); if (cd && cd.shape) setTagOnce('builder_shape', cd.shape); } catch (e2) {} }
      }
    } catch (e) {}

    // --- final-ring arrival time (for dwell-before-add) ---
    if (STAGE === 'step3_final_ring') { try { sessionStorage.setItem(SS + 'fr_at', String(Date.now())); } catch (e) {} }

    // --- CART: join key (cart token) + value band + explicit in-cart flag ---
    if (STAGE === 'cart') {
      tag('builder_in_cart', /(^|;\s*)_ring=/.test(document.cookie) ? 'yes' : 'no');
      try {
        fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); }).then(function (c) {
          try {
            // builder_cart_token = THE join key: the orders webhook can persist
            // order.cart_token in Supabase so an order reconciles to this Clarity session.
            if (c && c.token) { setTagOnce('builder_cart_token', c.token); cl5('identify', c.token); }
            if (c && typeof c.total_price === 'number') setTagOnce('builder_cart_value_band', band(c.total_price / 100, PB));
          } catch (e3) {}
        }).catch(function () {});
      } catch (e) {}
    }
  }

  /* ---- 3) ASYNC "USABLE vs BLANK" DETECTION (diamond PDP + final ring) --- *
   * Both pages render a skeleton first and hydrate from an API. We must tell *
   * "arrived" from "actually usable", and catch the silent dead-ends.        */
  function watchHydration(opts) {
    // opts: { okSel, okStyleNonNone, blankSel, loadedEvt, blankEvt, blankWhich, stuckEvt }
    var done = false;
    function visible(sel) {
      try { var el = document.querySelector(sel); if (!el) return false;
        var s = getComputedStyle(el); return s.display !== 'none' && s.visibility !== 'hidden'; }
      catch (e) { return false; }
    }
    function check() {
      if (done) return false;
      if (opts.blankSel && visible(opts.blankSel)) { done = true; leak(opts.blankEvt, opts.blankWhich); return true; }
      if (opts.okSel && visible(opts.okSel))       { done = true; fireOnce(opts.loadedEvt, opts.loadedEvt); return true; }
      return false;
    }
    var mo;
    try {
      mo = new MutationObserver(function () { if (check() && mo) mo.disconnect(); });
      mo.observe(document.documentElement, { attributes: true, childList: true, subtree: true, attributeFilter: ['style', 'class'] });
    } catch (e) {}
    if (check() && mo) mo.disconnect();
    // stuck-skeleton watchdog: neither usable nor an explicit blank state after 8s.
    // 8s (not 6) clears Supabase edge-fn cold starts so a slow-but-OK hydrate isn't
    // mislabeled "stuck".
    setTimeout(function () {
      if (done) return; done = true; if (mo) mo.disconnect();
      leak(opts.stuckEvt, opts.stuckWhich);     // silent stuck/loading dead-end
    }, 8000);
  }

  /* ---- 4) WIRE EVERYTHING ONCE THE DOM IS READY ------------------------- */
  function onReady() {
    // 4a. async hydration watchers
    if (STAGE === 'step2_diamond_pdp') {
      watchHydration({
        okSel: '#mainproduct', blankSel: '#noproduct',
        loadedEvt: 'bld_diamond_pdp_loaded',
        blankEvt: 'bld_diamond_pdp_blank', blankWhich: 'diamond_pdp_unavailable',
        stuckEvt: 'bld_diamond_pdp_stuck',  stuckWhich: 'diamond_pdp_stuck_skeleton'
      });
    }
    if (STAGE === 'step3_final_ring') {
      watchHydration({
        okSel: '#finalmainsection', blankSel: '#noproduct',
        loadedEvt: 'bld_final_ring_loaded',
        blankEvt: 'bld_final_ring_blank', blankWhich: 'final_ring_unavailable',
        stuckEvt: 'bld_final_ring_stuck',  stuckWhich: 'final_ring_stuck_skeleton'
      });
      // diamond param missing on a final-ring URL = guaranteed dead-end
      if (!qp('diamond')) leak('bld_final_ring_no_diamond_param', 'final_ring_missing_diamond_param');
    }
    if (STAGE === 'step2_diamond_catalog') {
      // zero-result dead-end (modern .sd-empty / legacy .empty-diamond)
      var checkEmpty = function () {
        try {
          if (document.querySelector('#sd-grid .sd-empty, #sd-widen-btn, #diamondtable .empty-diamond')) {
            leak('bld_diamond_catalog_empty', 'diamond_catalog_zero_results');
          }
        } catch (e) {}
      };
      setTimeout(checkEmpty, 2500); setTimeout(checkEmpty, 6000);
    }

    // 4b. delegated clicks for funnel-progression INTENT
    document.addEventListener('click', function (e) {
      try {
        var n = e.target && e.target.closest ? e.target : (e.target && e.target.parentNode);
        if (!n || !n.closest) return;

        // Step1 -> Step2 intent: "Choose this setting"
        if (n.closest('#choosethissetting')) { evt('bld_choose_setting_click'); keep('builder_progress'); return; }

        // Diamond grid card -> diamond PDP  (also stash vendor/shape/price for the session)
        if (n.closest('a.sd-card') || n.closest(".diamond-card a[href*='/products/diamonds']")) {
          evt('bld_diamond_card_click');
          try {
            var sd = n.closest('a.sd-card');   // modern grid carries the metadata we need
            if (sd) {
              var shEl = sd.querySelector('.sd-card-shape');
              var prEl = sd.querySelector('.sd-card-price span') || sd.querySelector('.sd-card-price');
              sessionStorage.setItem(SS + 'dia', JSON.stringify({
                shape: shEl ? (shEl.textContent || '').trim() : '',
                vendor: sd.querySelector('.sd-card-crest') ? 'stienhard' : 'nivoda', // crest = Cut In House
                price: prEl ? parsePrice(prEl.textContent) : 0
              }));
            }
          } catch (e3) {}
          return;
        }

        // Step2 -> Step3 intent: "Add to Ring" (navigation, NOT a cart add)
        if (n.closest('#addtoring')) {
          evt('bld_add_to_ring_click'); keep('builder_progress');
          // leak: no setting chosen yet -> bounces back to setting selection
          if (!/(^|;\s*)_ring=/.test(document.cookie)) evt('bld_diamond_first_to_setting'); // 2026-06-23: NOT a leak \u2014 normal diamond-first transition into setting selection.
          return;
        }

        // Step3 add-to-cart CLICK (intent; success/fail tracked via fetch wrap below)
        if (n.closest('#addToCart')) {
          evt('bld_add_to_cart_click');
          // dwell: how long they deliberated on the final-ring page before adding
          try { var frAt = parseInt(sessionStorage.getItem(SS + 'fr_at') || '0', 10);
            if (frAt) setTagOnce('builder_finalring_dwell_secs', band((Date.now() - frAt) / 1000, [3, 10, 30, 120])); } catch (e4) {}
          window.__spAddClickAt = Date.now();
          window.__spAddFetchedAt = 0;
          window.__spAddResolved = false;   // becomes true once this click reaches ANY terminal state
          // ring-size gate: empty size -> finalproduct.js returns early & shows .error-msg
          try {
            var sizeSel = document.querySelector('#product-single__ring-size');
            if (sizeSel && !sizeSel.value) { leak('bld_add_to_cart_size_blocked', 'ring_size_not_selected'); window.__spAddResolved = true; }
          } catch (e2) {}
          // silent "no request" watchdog: catches a GENUINE no-request abort (e.g. the
          // empty-diamond early return in finalproduct.js). Bails if the add already
          // resolved (size-blocked / success / failure) or a real /cart/add.js request
          // started. 8s comfortably clears Supabase nivoda-createproduct cold starts, so
          // a slow-but-SUCCESSFUL add is never double-counted as a failure.
          clearTimeout(window.__spAddWatchdog);
          window.__spAddWatchdog = setTimeout(function () {
            if (!window.__spAddResolved && !window.__spAddFetchedAt &&
                window.location.pathname.indexOf('/cart') !== 0) {
              window.__spAddResolved = true;
              leak('bld_add_to_cart_failed', 'silent_no_request');
            }
          }, 8000);
          return;
        }

        // Buy-loose (loose stone, leaves the ring funnel)
        if (n.closest('#createproduct')) { evt('bld_buy_loose_click'); return; }

        // Reached checkout (native form submit; cover all 3 button instances)
        if (n.closest("button[name='checkout']")) { window.__spReachedCheckout = true; evtDurable('bld_reached_checkout'); keep('reached_checkout'); return; }
      } catch (err) {}
    }, true);

    // 4c. ring-size selection
    document.addEventListener('change', function (e) {
      try {
        if (e.target && e.target.id === 'product-single__ring-size' && e.target.value) {
          var sizer = e.target.value === 'ring_sizer_request';
          evt(sizer ? 'bld_ring_sizer_requested' : 'bld_ring_size_selected');
        }
      } catch (err) {}
    }, true);

    // 4d. scroll-depth milestones (only on builder pages), once each, tagged by stage
    if (STAGE) {
      var hit = {};
      var onScroll = function () {
        try {
          var d = document.documentElement, b = document.body;
          var h = Math.max(d.scrollHeight, b.scrollHeight) - window.innerHeight;
          if (h <= 0) return;
          var pct = Math.min(100, Math.round(((window.pageYOffset || d.scrollTop) / h) * 100));
          [50, 75, 90].forEach(function (m) {
            if (pct >= m && !hit[m]) { hit[m] = 1; evt('bld_scroll_' + m); }
          });
          if (hit[50] && hit[75] && hit[90]) window.removeEventListener('scroll', onScroll);
        } catch (e) {}
      };
      window.addEventListener('scroll', onScroll, { passive: true });
    }

    // 4e. cart abandonment: left the cart page (page hidden/unloading) without
    //     ever clicking checkout. The funnel's biggest known drop is cart->checkout.
    if (STAGE === 'cart') {
      var abandoned = false;
      var onLeaveCart = function () {
        if (abandoned || window.__spReachedCheckout) return;
        abandoned = true;
        evt('bld_cart_abandoned'); keep('cart_abandoned');
      };
      window.addEventListener('pagehide', onLeaveCart);
      document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'hidden') onLeaveCart();
      });
    }
  }

  /* ---- 5) CART-ADD SUCCESS/FAILURE via fetch wrap (decoupled, safe) ------ */
  try {
    var origFetch = window.fetch;
    if (origFetch && !origFetch.__spWrapped) {
      var wrapped = function (input, init) {
        var url = '', method = '';
        try {
          url = typeof input === 'string' ? input : (input && input.url) || '';
          method = (init && init.method) || (input && input.method) || 'GET';
        } catch (e) {}
        var isCartAdd = /\/cart\/add(\.js)?(\?|$)/.test(url) && /post/i.test(method);
        // cart edit/remove on the cart page (AJAX change/update) -> engagement signal
        if (STAGE === 'cart' && /\/cart\/(change|update)(\.js)?(\?|$)/.test(url) && /post/i.test(method)) {
          try { evt('bld_cart_changed'); } catch (e) {}
        }
        var p = origFetch.apply(this, arguments);
        if (isCartAdd) {
          window.__spAddFetchedAt = Date.now();
          clearTimeout(window.__spAddWatchdog);   // a real request started -> not a "silent no request"
          // Only attribute to the builder if a builder Add-to-Cart click is recent
          // (<15s). Prevents a future 3rd-party fetch /cart/add.js (upsell/sticky-cart)
          // on a non-builder page from polluting the builder conversion count.
          var fromBuilder = !!(window.__spAddClickAt && (Date.now() - window.__spAddClickAt < 15000));
          try {
            p.then(function (resp) {
              try {
                window.__spAddResolved = true;
                if (resp && resp.ok) { if (fromBuilder) { evtDurable('bld_add_to_cart_success'); keep('add_to_cart_success'); } }
                else if (fromBuilder) { leak('bld_add_to_cart_failed', 'cart_add_http_error'); }
              } catch (e) {}
            }, function () { window.__spAddResolved = true; if (fromBuilder) leak('bld_add_to_cart_failed', 'cart_add_network_error'); });
          } catch (e) {}
        }
        return p;
      };
      wrapped.__spWrapped = true;
      window.fetch = wrapped;
    }
  } catch (e) {}

  /* ---- 6) GLOBAL JS-ERROR capture on builder pages (the script-error leak) */
  if (STAGE) {
    var errFired = 0;
    function onErr(msg) {
      if (errFired > 3) return; errFired++;            // cap noise
      var m = (msg == null) ? '' : String(msg);
      // redact URLs / emails / long tokens before sending the error class to Clarity
      m = m.replace(/https?:\/\/[^\s'"]+/gi, '[url]')
           .replace(/[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}/g, '[email]')
           .replace(/[A-Za-z0-9_\-]{24,}/g, '[tok]');
      if (m && m !== 'undefined') { try { tag('builder_js_error', m.slice(0, 120)); } catch (e) {} }
      leak('bld_js_error', 'js_error_on_builder');     // fire even with no message
    }
    // Only count errors thrown by the BUILDER's own scripts — the live theme's heavy
    // legacy/3rd-party JS throws unrelated errors that would otherwise drown the signal.
    window.addEventListener('error', function (e) {
      try { var src = (e && e.filename) || '';
        if (!/finalproduct|diamonddetail|sp-funnel-clarity/i.test(src)) return;
      } catch (x) { return; }
      onErr(e && e.message);
    }, true);
    // unhandledrejection has no reliable source; only count it on the API-hydration
    // builder pages where a rejected fetch/axios is most likely builder-relevant.
    window.addEventListener('unhandledrejection', function (e) {
      if (STAGE !== 'step2_diamond_pdp' && STAGE !== 'step3_final_ring') return;
      onErr(e && e.reason && (e.reason.message || e.reason));
    });
  }

  /* ---- boot ------------------------------------------------------------- */
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', onReady);
  else onReady();
})();
